/* Graph of Life brand v1.1.0 · tokens.json */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { getProof } from '@/data/proofRegistry';

// Interactive map of the computational background. Each discipline node
// resolves to the proof systems it actually produced.
const NODES = [
  {
    id: 'systems-bio',
    label: 'Computational systems biology',
    x: 20,
    y: 24,
    detail:
      'Postdoctoral work on nonlinear biological interaction and trophic dynamics. This is where the discipline of preserving mechanism over averaging comes from.',
    proofs: ['ai-ecologist'],
  },
  {
    id: 'bioinformatics',
    label: 'Marine bioinformatics',
    x: 50,
    y: 12,
    detail:
      'Genome-resolved analysis of sediment and marine metagenomes, including the methane pathways that sit underneath a blue-carbon claim.',
    proofs: ['methanet'],
  },
  {
    id: 'graph',
    label: 'Knowledge graphs',
    x: 78,
    y: 26,
    detail:
      'Governed graph architecture spanning 30.5M nodes and 170M relationships in the biodiversity graph, with per-claim evidence tiers in BlueGraph.',
    proofs: ['bluegraph'],
  },
  {
    id: 'ml',
    label: 'Graph and sequence learning',
    x: 84,
    y: 62,
    detail:
      'Link prediction over ecological networks and language-model architectures applied to DNA, RNA and protein sequences as structured evidence.',
    proofs: ['ai-ecologist', 'bioprior'],
  },
  {
    id: 'geospatial',
    label: 'Earth observation',
    x: 55,
    y: 84,
    detail:
      'Hyperspectral and multi-sensor evidence, tested against field-verified biology before any equivalence is assumed.',
    proofs: ['bioprior', 'fluvion'],
  },
  {
    id: 'hydro',
    label: 'Watershed dependency',
    x: 22,
    y: 72,
    detail:
      'Moisture transport and parcel-level watershed dependency, scored against real monitoring stations with the losses published.',
    proofs: ['fluvion', 'upper-colorado'],
  },
  {
    id: 'capital',
    label: 'Natural-capital finance',
    x: 50,
    y: 48,
    core: true,
    detail:
      'Where the other six meet. Evidence is organized so a risk, investment or diligence team can inspect it, with the current boundary stated on the record.',
    proofs: ['bluegraph', 'methanet'],
  },
];

const EDGES = [
  ['systems-bio', 'capital'],
  ['bioinformatics', 'capital'],
  ['graph', 'capital'],
  ['ml', 'capital'],
  ['geospatial', 'capital'],
  ['hydro', 'capital'],
  ['systems-bio', 'bioinformatics'],
  ['bioinformatics', 'graph'],
  ['graph', 'ml'],
  ['ml', 'geospatial'],
  ['geospatial', 'hydro'],
  ['hydro', 'systems-bio'],
];

const find = (id) => NODES.find((n) => n.id === id);

export const CapabilityGraph = () => {
  const [active, setActive] = useState('capital');
  const node = find(active);

  return (
    <div data-testid="capability-graph" className="grid gap-6 lg:grid-cols-[1.35fr_1fr] items-stretch">
      <div className="gol-panel gol-panel-live gol-scan relative overflow-hidden p-5 md:p-6">
        <div className="gol-grid absolute inset-0 opacity-70 pointer-events-none" />
        <div className="relative z-10 aspect-square sm:aspect-[4/3] w-full">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            {EDGES.map(([a, b]) => {
              const s = find(a);
              const t = find(b);
              const lit = active === a || active === b;
              return (
                <line
                  key={`${a}-${b}`}
                  x1={s.x}
                  y1={s.y}
                  x2={t.x}
                  y2={t.y}
                  stroke={lit ? 'rgba(45,212,191,0.8)' : 'rgba(45,212,191,0.2)'}
                  strokeWidth={lit ? 1 : 0.5}
                  vectorEffect="non-scaling-stroke"
                  style={{ transition: 'stroke 0.4s ease, stroke-width 0.4s ease' }}
                />
              );
            })}
          </svg>

          {NODES.map((n) => {
            const isActive = active === n.id;
            return (
              <motion.button
                key={n.id}
                type="button"
                data-testid={`capability-node-${n.id}`}
                onMouseEnter={() => setActive(n.id)}
                onFocus={() => setActive(n.id)}
                onClick={() => setActive(n.id)}
                className="absolute -translate-x-1/2 -translate-y-1/2 z-10 group"
                style={{ left: `${n.x}%`, top: `${n.y}%` }}
                whileHover={{ scale: 1.08 }}
                aria-pressed={isActive}
              >
                <span
                  className={`block rounded-full transition-all duration-500 ${
                    n.core ? 'w-4 h-4' : 'w-2.5 h-2.5'
                  } ${
                    isActive
                      ? 'bg-teal-300 shadow-[0_0_26px_rgba(45,212,191,0.75)]'
                      : 'bg-teal-500/70 shadow-[0_0_12px_rgba(45,212,191,0.35)]'
                  }`}
                />
                <span
                  className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 w-32 text-center font-mono text-[9px] uppercase tracking-widest transition-colors duration-300 ${
                    isActive ? 'text-teal-300' : 'text-sand-50/40'
                  }`}
                >
                  {n.label}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className="gol-panel gol-panel-lead p-6 md:p-8 flex flex-col gap-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-teal-400">
          {node.core ? 'Where it converges' : 'Discipline'}
        </p>
        <h3 className="font-serif text-2xl md:text-3xl text-sand-50 leading-tight">{node.label}</h3>
        <p className="text-sm md:text-base text-sand-50/70 leading-relaxed">{node.detail}</p>

        <div className="mt-auto pt-4 border-t border-abyss-800 space-y-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand-50/40">Produced</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {node.proofs.map((id) => {
              const proof = getProof(id);
              if (!proof) return null;
              return (
                <a
                  key={id}
                  href={proof.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`capability-proof-${id}`}
                  className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-teal-400 hover:text-sand-50 transition-colors duration-200"
                >
                  {proof.name}
                  <ArrowUpRight size={11} aria-hidden="true" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapabilityGraph;
